import React from 'react';
import {
  UserPlus, MailCheck, Search, CreditCard, Coins, Unlock, User, Phone, Mail, MapPin, MessageSquare,
  Trophy, Clock, CheckCircle2, Lock, Star,
} from 'lucide-react';
import { TutorialStepId } from '@/data/tutorial';
import { LEAD_PRICE, PLAN_INCLUDED_LEADS, PLAN_PRICES, money, Lang } from '@/data/constants';

/**
 * Illustrative, non-interactive mock screens for the tutorial player. They are
 * drawn in JSX (not screenshots) so they stay crisp, bilingual and in sync with
 * live pricing from constants.ts. Sample data is clearly fictional.
 */

const Frame: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl">
    <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2">
      <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
      <span className="ml-2 truncate text-[11px] font-semibold text-slate-400">giggone.com{title}</span>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: string; delay?: number }> = ({ label, value, delay = 0 }) => (
  <div className="animate-fade-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}>
    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <div className="mt-0.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800">{value}</div>
  </div>
);

const Btn: React.FC<{ children: React.ReactNode; tone?: 'orange' | 'navy' | 'green' }> = ({ children, tone = 'orange' }) => (
  <div
    className={`mt-4 flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white shadow-lg ring-4 ring-offset-0 animate-pulse ${
      tone === 'orange' ? 'bg-orange-500 ring-orange-200' : tone === 'green' ? 'bg-emerald-600 ring-emerald-200' : 'bg-slate-900 ring-slate-200'
    }`}
  >
    {children}
  </div>
);

const TutorialScene: React.FC<{ id: TutorialStepId; lang: Lang; gc?: boolean }> = ({ id, lang, gc }) => {
  const es = lang === 'es';

  switch (id) {
    case 'account':
      return (
        <Frame title={`/${lang}/register`}>
          <p className="flex items-center gap-2 text-base font-extrabold text-slate-900">
            <UserPlus className="h-5 w-5 text-orange-500" /> {es ? 'Crear cuenta' : 'Create account'}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className={`rounded-lg border-2 px-2 py-2 text-center text-xs font-bold ${!gc ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-500'}`}>
              {es ? 'Subcontratista' : 'Subcontractor'}
            </div>
            <div className={`rounded-lg border-2 px-2 py-2 text-center text-xs font-bold ${gc ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-500'}`}>
              {es ? 'Contratista General' : 'General Contractor'}
            </div>
          </div>
          <div className="mt-3 grid gap-2">
            <Field label={es ? 'Empresa' : 'Company'} value="Rivera Roofing LLC" delay={100} />
            <Field label={es ? 'Oficios' : 'Trades'} value={es ? 'Techado · Canaletas' : 'Roofing · Gutters'} delay={250} />
            <Field label={es ? 'Zona de servicio' : 'Service area'} value="Fontana, CA · 25 mi" delay={400} />
          </div>
          <Btn>{es ? 'Crear cuenta gratis' : 'Create free account'}</Btn>
        </Frame>
      );
    case 'verify':
      return (
        <Frame title=" · inbox">
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-[11px] font-bold text-slate-400">GIGGONE Support</p>
            <p className="text-sm font-extrabold text-slate-900">{es ? 'Confirma tu correo de GIGGONE' : 'Confirm your GIGGONE email address'}</p>
            <p className="mt-1 text-xs text-slate-500">
              {es ? 'El enlace funciona una vez y vence en 24 horas.' : 'The link works once and expires in 24 hours.'}
            </p>
            <Btn>{es ? 'Confirmar mi correo' : 'Confirm my email'}</Btn>
          </div>
          <p className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-emerald-50 py-2 text-sm font-bold text-emerald-700 animate-fade-in" style={{ animationDelay: '900ms', animationFillMode: 'both' }}>
            <MailCheck className="h-4 w-4" /> {es ? 'Correo verificado' : 'Email verified'}
          </p>
        </Frame>
      );
    case 'browse':
      return (
        <Frame title={`/${lang}/jobs`}>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-400">
            <Search className="h-3.5 w-3.5" /> {es ? 'techado, Fontana…' : 'roofing, Fontana…'}
          </div>
          <div className="mt-3 space-y-2">
            {[
              [es ? 'Reemplazo de techo' : 'Roof replacement', 'Fontana, CA', '$12,000 – $18,000'],
              [es ? 'Remodelación de cocina' : 'Kitchen remodel', 'Rancho Cucamonga, CA', '$25,000+'],
              [es ? 'Panel eléctrico 200A' : '200A panel upgrade', 'Ontario, CA', '$3,500 – $5,000'],
            ].map(([t, c, b], i) => (
              <div key={t} className="flex items-center justify-between rounded-xl border border-slate-200 p-3 animate-fade-in" style={{ animationDelay: `${i * 180}ms`, animationFillMode: 'both' }}>
                <div>
                  <p className="text-sm font-bold text-slate-900">{t}</p>
                  <p className="text-[11px] text-slate-500">{c} · {b}</p>
                </div>
                <span className="flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-500">
                  <Lock className="h-3 w-3" /> {es ? 'Oculto' : 'Hidden'}
                </span>
              </div>
            ))}
          </div>
        </Frame>
      );
    case 'buy':
      return (
        <Frame title={`/${lang}/pricing`}>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border-2 border-orange-500 bg-orange-50 p-3">
              <p className="text-[10px] font-black uppercase text-orange-700">{es ? '1 lead' : 'Single lead'}</p>
              <p className="text-2xl font-black text-slate-900">{money(LEAD_PRICE)}</p>
              <p className="text-[11px] text-slate-500">{es ? 'Precio fijo' : 'Flat price'}</p>
            </div>
            {(['basic', 'pro', 'premium'] as const).map((p, i) => (
              <div key={p} className="rounded-xl border border-slate-200 p-3 animate-fade-in" style={{ animationDelay: `${(i + 1) * 150}ms`, animationFillMode: 'both' }}>
                <p className="text-[10px] font-black uppercase text-slate-500">{p}</p>
                <p className="text-lg font-black text-slate-900">{money(PLAN_PRICES[p])}<span className="text-[10px] text-slate-400">/mo</span></p>
                <p className="text-[11px] text-slate-500">{PLAN_INCLUDED_LEADS[p]} {es ? 'leads' : 'leads'}</p>
              </div>
            ))}
          </div>
          <Btn tone="navy"><CreditCard className="h-4 w-4" /> {es ? 'Pago seguro' : 'Secure checkout'}</Btn>
        </Frame>
      );
    case 'credits':
      return (
        <Frame title={`/${lang}/dashboard`}>
          <div className="rounded-2xl bg-slate-900 p-5 text-white">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{es ? 'Saldo de leads' : 'Lead balance'}</p>
            <p className="mt-1 flex items-center gap-2 text-4xl font-black">
              <Coins className="h-8 w-8 text-orange-400" /> 10
            </p>
            <p className="mt-1 text-xs text-slate-300">{es ? 'créditos disponibles · 1 crédito = 1 lead' : 'credits available · 1 credit = 1 lead'}</p>
          </div>
          <p className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 animate-fade-in" style={{ animationDelay: '500ms', animationFillMode: 'both' }}>
            <CheckCircle2 className="h-4 w-4" /> {es ? 'Pago confirmado — créditos agregados' : 'Payment confirmed — credits added'}
          </p>
        </Frame>
      );
    case 'unlock':
      return (
        <Frame title={`/${lang}/jobs/roof-replacement-fontana`}>
          <p className="text-base font-extrabold text-slate-900">{es ? 'Reemplazo de techo' : 'Roof replacement'}</p>
          <p className="text-xs text-slate-500">Fontana, CA · $12,000 – $18,000</p>
          <div className="mt-3 space-y-1.5 rounded-xl bg-slate-50 p-3 blur-[2px]">
            <div className="h-3 w-3/4 rounded bg-slate-300" />
            <div className="h-3 w-1/2 rounded bg-slate-300" />
            <div className="h-3 w-2/3 rounded bg-slate-300" />
          </div>
          <p className="mt-2 text-[11px] font-semibold text-slate-500">{es ? '1 de 3 contratistas · quedan 2 lugares' : '1 of 3 contractors · 2 spots left'}</p>
          <Btn><Unlock className="h-4 w-4" /> {es ? 'Desbloquear Lead · 1 crédito' : 'Unlock Lead · 1 credit'}</Btn>
        </Frame>
      );
    case 'info':
      return (
        <Frame title={`/${lang}/dashboard#my-leads`}>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> {es ? 'Lead desbloqueado' : 'Lead unlocked'}
          </p>
          <div className="mt-3 space-y-2">
            {[
              [User, 'Maria G.'],
              [Phone, '(909) 555-0142'],
              [Mail, 'maria.g@example.com'],
              [MapPin, '1234 Sample Ave, Fontana, CA 92335'],
            ].map(([Icon, v], i) => {
              const I = Icon as typeof User;
              return (
                <div key={String(v)} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 animate-fade-in" style={{ animationDelay: `${i * 200}ms`, animationFillMode: 'both' }}>
                  <I className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-semibold text-slate-800">{String(v)}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-center text-[10px] text-slate-400">{es ? 'Datos de ejemplo' : 'Sample data'}</p>
        </Frame>
      );
    case 'contact':
      return (
        <Frame title={`/${lang}/dashboard#my-leads`}>
          <div className="grid grid-cols-3 gap-2">
            {[
              [Phone, es ? 'Llamar' : 'Call', 'bg-emerald-600'],
              [MessageSquare, es ? 'Texto' : 'Text', 'bg-blue-600'],
              [Mail, es ? 'Correo' : 'Email', 'bg-slate-900'],
            ].map(([Icon, l, c], i) => {
              const I = Icon as typeof Phone;
              return (
                <div key={String(l)} className={`flex flex-col items-center gap-1 rounded-xl ${c} py-3 text-xs font-bold text-white ${i === 0 ? 'animate-pulse' : ''}`}>
                  <I className="h-5 w-5" /> {String(l)}
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-wide text-slate-400">{es ? 'Etapa del lead' : 'Lead stage'}</p>
          <div className="mt-1.5 flex gap-1.5">
            {(es ? ['Comprado', 'Contactado', 'Cotizado', 'Ganado'] : ['Purchased', 'Contacted', 'Quoted', 'Won']).map((s, i) => (
              <span key={s} className={`flex-1 rounded-md px-1.5 py-1.5 text-center text-[10px] font-bold ${i <= 1 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {s}
              </span>
            ))}
          </div>
        </Frame>
      );
    case 'win':
    default:
      return (
        <Frame title=" · tips">
          <div className="space-y-2">
            {[
              [Clock, es ? 'Llame en la primera hora' : 'Call within the first hour'],
              [MessageSquare, es ? 'Sin respuesta: mensaje + texto' : 'No answer: voicemail + text'],
              [Star, es ? 'Perfil completo con fotos' : 'Complete profile with photos'],
              [Trophy, es ? 'Cotice en 24 h y haga seguimiento' : 'Quote in 24h and follow up'],
            ].map(([Icon, l], i) => {
              const I = Icon as typeof Clock;
              return (
                <div key={String(l)} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 animate-fade-in" style={{ animationDelay: `${i * 180}ms`, animationFillMode: 'both' }}>
                  <I className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-semibold text-slate-800">{String(l)}</span>
                </div>
              );
            })}
          </div>
        </Frame>
      );
  }
};

export default TutorialScene;
